
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials';
import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold';
import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import LinkPlugin from '@ckeditor/ckeditor5-link/src/link';
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import Font from '@ckeditor/ckeditor5-font/src/font';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat';
import List from '@ckeditor/ckeditor5-list/src/list';
import { Lead, LeadStatus } from '@/types';

moment.locale('it');

@Component
export default class Editor extends Vue {
  @Prop(String) readonly value!: string;
  @Prop(String) readonly title: string | undefined
  @Prop(String) readonly updatedAt: string | null | undefined
  @Prop(String) readonly updatedBy: string | null | undefined
  @Prop({ type: Boolean, default: false }) readonly loading!: boolean
  @Prop({ type: Boolean, default: false }) readonly simple!: boolean
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean
  @Prop({ type: Boolean, default: false }) readonly embedded!: boolean

  editor = ClassicEditor

  editedContent = ''

  notify = false

  editorConfig = {
    placeholder: 'Inserisci qui il testo',
    language: 'it',
    plugins: [
      EssentialsPlugin,
      BoldPlugin,
      ItalicPlugin,
      Underline,
      LinkPlugin,
      ParagraphPlugin,
      Alignment,
      Font,
      RemoveFormat,
      List,
    ],
    toolbar: {
      items: [
        'bold', 'italic', 'underline',
        '|',
        'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor',
        '|',
        'alignment:left', 'alignment:right', 'alignment:center',
        '|',
        'link', 'numberedList', 'bulletedList',
        '|',
        'undo', 'redo', 'removeFormat',
      ],
      shouldNotGroupWhenFull: true,
    },
  }

  editing = false
  changed = false

  cancelConfirmDialog = false
  closeConfirmDialog = false

  created() {
    // eslint-disable-next-line no-console
    console.log('Editor: created()');

    this.editedContent = this.value ?? '';
    this.changed = false;

    // Attiva subito l'editor se non occorre la gestione del salvataggio,
    // annullamento, ecc. (se non readonly)
    if (this.simple && !this.readonly) {
      this.editing = true;
    }
  }

  @Watch('value')
  onValueChange(val: string) {
    // eslint-disable-next-line no-console
    console.log('Editor: watch(value)');

    if (!this.editing) {
      this.editedContent = val ?? '';
      this.changed = false;
    } else {
      // eslint-disable-next-line no-console
      console.log('Editor: watch(value) evito di aggiornare il contenuto perché è in modifica');
    }
  }

  @Watch('editedContent')
  onEditedContentChange(val: string) {
    // eslint-disable-next-line no-console
    console.log('Editor: watch(editedContent)');

    if (val !== this.value) {
      this.changed = true;

      // Aggiurna subito il contenuto se non occorre la gestione del salvataggio,
      // annullamento, ecc.
      if (this.simple) {
        this.$emit('input', val);
      }
    }
  }

  @Watch('editing')
  onEditing(val: boolean) {
    console.log('Editor: onEditing'); // eslint-disable-line no-console

    this.$emit('editing', val);
  }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get status(): LeadStatus {
    return this.lead.status;
  }

  get closed(): boolean {
    return this.lead && this.status === 'closed';
  }

  get resolved(): boolean {
    return this.lead && this.status === 'resolved';
  }

  get rejected(): boolean {
    return this.lead && this.status === 'rejected';
  }

  get updatedAtLong() {
    if (!this.updatedAt) return '';

    if (this.updatedBy) {
      return `Ultima modifica: ${moment(this.updatedAt).format('L')} ore ${moment(this.updatedAt).format('LT')} `
        + `(${this.updatedBy ?? 'Sconosciuto'})`;
    }

    return `Ultima modifica: ${moment(this.updatedAt).format('L')} ore ${moment(this.updatedAt).format('LT')}`;
  }

  onEditorFocus() {
    this.$emit('focus');
  }

  onEditorBlur() {
    this.$emit('blur');
  }

  onEditorInput() {
    // this.$emit('input');
  }

  onEdit() {
    this.editing = true;
  }

  onSave() {
    this.editing = false;
    this.$emit('save', {
      content: this.editedContent,
      notify: this.notify,
    });
  }

  onCancel() {
    // eslint-disable-next-line no-console
    console.log('Editor: onCancel()');

    if (!this.changed) {
      // Se il contenuto non è stato modificate,
      // interrompe la modifica senza chiedere conferme.
      this.editing = false;
    } else {
      // Chiede conferma prima di annullare le modifiche effettuate
      this.cancelConfirmDialog = true;
    }
  }

  onCancelConfirm() {
    // eslint-disable-next-line no-console
    console.log('Editor: onCancelConfirm()');

    this.editing = false;
    this.editedContent = this.value ?? '';
    this.changed = false;
    this.cancelConfirmDialog = false;
  }

  onClose() {
    console.log(`${this.constructor.name}: onClose()`); // eslint-disable-line no-console

    if (!this.changed) {
      // Se il contenuto non è stato modificate,
      // interrompe la modifica senza chiedere conferme.
      this.editing = false;

      // Comunica che va chiuso il dialog dentro il cuola l'editor e embedded
      this.$emit('close');
    } else {
      // Chiede conferma prima di annullare le modifiche effettuate
      this.closeConfirmDialog = true;
    }
  }

  onCloseConfirm() {
    console.log(`${this.constructor.name}: onCloseConfirm()`); // eslint-disable-line no-console

    this.editing = false;
    this.editedContent = this.value ?? '';
    this.changed = false;
    this.cancelConfirmDialog = false;

    // Comunica che va chiuso il dialog dentro il cuola l'editor e embedded
    this.$emit('close');
  }
}
